import React, { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

/**
 * Standalone login screen for the BSVAC Event Triage tool.
 *
 * Distinct from the main /login page — no back-links to the operations
 * console, its own branding, and it redirects into /triage after auth.
 * Uses the same BSVAC accounts (login accepts email OR username), so a
 * member does not need a separate password.
 */
const LOGO_BSVAC = "https://customer-assets.emergentagent.com/job_bedstuy-ems-hub/artifacts/rcm1qmbk_BSVAC%20Patch.png";

export default function TriageLogin() {
  const { user, login } = useAuth();
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();

  if (user) return <Navigate to="/triage" replace />;

  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    try {
      await login(identifier, password);
      nav("/triage", { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Sign-in failed");
    } finally { setBusy(false); }
  }

  return (
    <div className="min-h-screen flex bg-slate-950" data-testid="triage-login-page">
      <div className="flex-1 flex items-center justify-center px-6 py-10">
        <div className="w-full max-w-sm text-slate-100">
          <div className="flex items-center gap-3 mb-8">
            <img src={LOGO_BSVAC} alt="BSVAC" className="h-14 w-14 object-contain" />
            <div className="leading-tight">
              <div className="tab-tag text-slate-400">BSVAC · Event Ops</div>
              <div className="font-display font-black tracking-tight text-lg">Event Triage</div>
            </div>
          </div>
          <h1 className="font-display text-3xl font-black tracking-tight mb-1">Event Triage</h1>
          <p className="text-slate-400 text-sm mb-6">Sign in to access the patient management system</p>

          <form onSubmit={submit} className="space-y-4" data-testid="triage-login-form">
            <div>
              <Label htmlFor="tri-id" className="tab-tag text-slate-300">Username / Email</Label>
              <Input id="tri-id" type="text" required value={identifier} onChange={(e) => setIdentifier(e.target.value)} className="rounded-sm bg-slate-900 border-slate-700 text-slate-100" placeholder="Enter your username or email" data-testid="triage-login-id" autoComplete="username" />
            </div>
            <div>
              <Label htmlFor="tri-pw" className="tab-tag text-slate-300">Password</Label>
              <Input id="tri-pw" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="rounded-sm bg-slate-900 border-slate-700 text-slate-100" placeholder="Enter your password" data-testid="triage-login-password" autoComplete="current-password" />
            </div>
            <Button type="submit" disabled={busy} className="w-full rounded-sm bg-white text-slate-900 hover:bg-slate-200 font-semibold" data-testid="triage-login-submit">
              {busy ? "Signing in…" : "Sign In"}
            </Button>
          </form>

          <p className="text-[11px] text-slate-500 mt-6 text-center">Event Triage © {new Date().getFullYear()} — BSVAC / CBVAC</p>
        </div>
      </div>
      <div className="hidden md:block flex-1 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-red-900/40 via-slate-950 to-blue-900/40" />
        <div className="absolute bottom-8 left-8 right-8 text-slate-100">
          <div className="font-display font-bold text-2xl">Professional Medical Standby Services</div>
          <div className="text-slate-300 text-sm mt-1">Real-time patient triage, documentation, and command post operations.</div>
        </div>
      </div>
    </div>
  );
}
